package br.com.texeratx.boardgametimeapp.ui.screens.games.create

import br.com.texeratx.boardgametimeapp.core.i18n.LanguageManager
import br.com.texeratx.boardgametimeapp.data.model.game.CreateGameRequest
import br.com.texeratx.boardgametimeapp.data.model.game.GameModeDTO
import br.com.texeratx.boardgametimeapp.domain.usecases.games.CreateGameUseCase
import br.com.texeratx.boardgametimeapp.domain.usecases.games.GetGameCategoriesUseCase
import br.com.texeratx.boardgametimeapp.domain.usecases.games.GetGameModesUseCase
import br.com.texeratx.boardgametimeapp.domain.usecases.games.SearchCatalogUseCase
import br.com.texeratx.boardgametimeapp.ui.screens.games.model.GameCategoryUI
import br.com.texeratx.boardgametimeapp.domain.usecases.player.GetPlayersNameUseCase
import br.com.texeratx.boardgametimeapp.ui.screens.games.model.CatalogGameUI
import br.com.texeratx.boardgametimeapp.ui.screens.games.model.PlayerOwnerUI
import cafe.adriel.voyager.core.model.ScreenModel
import cafe.adriel.voyager.core.model.screenModelScope
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.launch

class CreateGameViewModel(
    private val createGameUseCase: CreateGameUseCase,
    private val getModes: GetGameModesUseCase,
    private val getCategories: GetGameCategoriesUseCase,
    private val getPlayersName: GetPlayersNameUseCase,
    private val searchCatalog: SearchCatalogUseCase,
    private val lang: LanguageManager
) : ScreenModel {

    val state = MutableStateFlow(CreateGameState())
    val modes = MutableStateFlow<List<GameModeDTO>>(emptyList())
    val isLoadingOptions = MutableStateFlow(false)

    private var searchJob: Job? = null

    fun updateState(reducer: (CreateGameState) -> CreateGameState) {
        state.value = reducer(state.value)
    }

    fun openCatalogSearch() {
        updateState { it.copy(showCatalogSearch = true) }
    }

    fun dismissCatalogSearch() {
        searchJob?.cancel()
        updateState {
            it.copy(
                showCatalogSearch = false,
                catalogQuery = "",
                catalogResults = emptyList(),
                isSearchingCatalog = false
            )
        }
    }

    fun onCatalogQueryChange(query: String) {
        updateState { it.copy(catalogQuery = query) }
        searchJob?.cancel()
        if (query.isBlank()) {
            updateState { it.copy(catalogResults = emptyList(), isSearchingCatalog = false) }
            return
        }
        searchJob = screenModelScope.launch {
            delay(300)
            updateState { it.copy(isSearchingCatalog = true) }
            searchCatalog(query).fold(
                onSuccess = { list ->
                    updateState { it.copy(catalogResults = list, isSearchingCatalog = false) }
                },
                onFailure = { err ->
                    if (err is CancellationException) return@launch
                    updateState {
                        it.copy(
                            catalogResults = emptyList(),
                            isSearchingCatalog = false,
                            error = err.message
                        )
                    }
                }
            )
        }
    }

    fun onCatalogPicked(game: CatalogGameUI) {
        searchJob?.cancel()
        updateState {
            it.copy(
                catalogGameId = game.id,
                catalogName = game.name,
                catalogImageUrl = game.imageUrl,
                catalogMinPlayers = game.minPlayers,
                catalogMaxPlayers = game.maxPlayers,
                catalogDuration = game.duration,
                catalogBggUrl = game.bggUrl,
                showCatalogSearch = false,
                catalogQuery = "",
                catalogResults = emptyList(),
                isSearchingCatalog = false
            )
        }
    }

    fun onCategoryChange(categoryId: Long?) {
        updateState { it.copy(gameCategoryId = categoryId) }
    }

    fun saveGame(onSuccess: () -> Unit) {
        screenModelScope.launch {
            val s = state.value

            val catalogId = s.catalogGameId
            if (catalogId == null) {
                updateState { it.copy(error = lang.strings.value.selectCatalogFirst) }
                return@launch
            }
            if (s.gameModeId == null) {
                updateState { it.copy(error = lang.strings.value.categoryAndModeRequired) }
                return@launch
            }
            if (s.selectedOwners.isEmpty()) {
                updateState { it.copy(error = lang.strings.value.selectAtLeastOneOwner) }
                return@launch
            }

            updateState { it.copy(isSaving = true, error = null) }

            val result = createGameUseCase(
                CreateGameRequest(
                    gameId = catalogId,
                    gameModeId = s.gameModeId,
                    gameCategoryId = s.gameCategoryId,
                    spotifyUrl = s.spotifyUrl.ifBlank { null },
                    manualUrl = s.manualUrl.ifBlank { null },
                    status = 1,
                    ownerIds = s.selectedOwners.map { it.id }
                )
            )

            result.fold(
                onSuccess = {
                    updateState { it.copy(isSaving = false) }
                    onSuccess()
                },
                onFailure = { throwable ->
                    updateState {
                        it.copy(
                            isSaving = false,
                            error = throwable.message ?: lang.strings.value.errorCreatingGame
                        )
                    }
                }
            )
        }
    }

    fun loadModes() {
        screenModelScope.launch {
            isLoadingOptions.value = true
            getModes().onSuccess { modes.value = it }
            isLoadingOptions.value = false
        }
    }

    fun loadCategories() {
        screenModelScope.launch {
            getCategories().onSuccess { list ->
                updateState {
                    it.copy(
                        categories = list.map { dto ->
                            GameCategoryUI(id = dto.id, name = dto.name)
                        }
                    )
                }
            }
        }
    }

    fun loadOwners() {
        screenModelScope.launch {
            updateState { it.copy(isLoadingOwners = true) }
            getPlayersName().fold(
                onSuccess = { list ->
                    updateState { it.copy(owners = list, isLoadingOwners = false) }
                },
                onFailure = { throwable ->
                    updateState { it.copy(isLoadingOwners = false, error = throwable.message) }
                }
            )
        }
    }

    fun addOwner(owner: PlayerOwnerUI) {
        updateState { current ->
            if (current.selectedOwners.any { it.id == owner.id }) current
            else current.copy(selectedOwners = current.selectedOwners + owner)
        }
    }

    fun removeOwner(ownerId: Long) {
        updateState { current ->
            current.copy(selectedOwners = current.selectedOwners.filterNot { it.id == ownerId })
        }
    }
}
